import { ThemeEvents, VariantUpdateEvent, SellingPlanUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * It also listens for selling plan updates and overlays the subscription price
 * (with the one-time price as compare-at) while a plan is selected.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {string | null} Server-rendered price markup, cached while a selling plan price is shown */
  #basePriceHTML = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.sellingPlanUpdate, this.updateSellingPlanPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.sellingPlanUpdate, this.updateSellingPlanPrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // The container is replaced with fresh server markup, so any cached
    // pre-selling-plan state is stale. The subscription picker re-applies
    // its price on the next frame if a plan is still selected.
    this.#basePriceHTML = null;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Shows the selected selling plan's price (with the one-time price as
   * compare-at) or restores the server-rendered price for one-time purchases.
   * @param {SellingPlanUpdateEvent} event - The selling plan update event.
   */
  updateSellingPlanPrice = (event) => {
    const { productId } = event.detail.data;
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    const { planId, formattedPrice, formattedCompare } = event.detail.resource;

    if (planId && formattedPrice) {
      if (this.#basePriceHTML == null) {
        this.#basePriceHTML = priceContainer.innerHTML;
      }

      const regular = priceContainer.querySelector('.price__regular');
      const sale = priceContainer.querySelector('.price__sale');
      const salePrice = sale?.querySelector('.price-item--sale');
      const comparePrice = sale?.querySelector('.compare-at-price');

      if (sale && salePrice && formattedCompare && formattedCompare !== formattedPrice) {
        salePrice.textContent = formattedPrice;
        if (comparePrice) comparePrice.textContent = formattedCompare;
        sale.classList.remove('price__hidden');
        regular?.classList.add('price__hidden');
      } else {
        // No discount to show (or markup missing) - update the regular price
        const regularPrice = regular?.querySelector('.price');
        if (regularPrice) regularPrice.textContent = formattedPrice;
        regular?.classList.remove('price__hidden');
        sale?.classList.add('price__hidden');
      }
    } else if (this.#basePriceHTML != null) {
      priceContainer.innerHTML = this.#basePriceHTML;
      this.#basePriceHTML = null;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
